import { useState } from "react";
import { Maximize2, ChevronRight } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmtUSD } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SourceFooter } from "./WireTable";

const DIR_COLOR = { Incoming: "#3b82f6", Outgoing: "#e0a43c", Internal: "#8b7fd6" };

function ChartCard({ id, title, sub, source, values, children, big, action }) {
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  return (
    <div className="panel overflow-hidden flex flex-col" data-testid={`chart-${id}`}>
      <div className="px-5 py-3.5 flex justify-between items-start gap-2">
        <div><div className="text-sm font-semibold text-slate-900">{title}</div><div className="text-[11px] text-slate-500">{sub}</div></div>
        <div className="flex gap-1.5">{action}<button data-testid={`chart-${id}-expand`} onClick={() => setOpen(true)} className="btn-ghost"><Maximize2 className="w-3.5 h-3.5" />Expand</button></div>
      </div>
      <div className="px-3 flex-1 min-h-[240px]">{children(240)}</div>
      <div className="px-5 py-2 border-t border-slate-100">
        <button data-testid={`chart-${id}-values-toggle`} onClick={() => setShow(!show)} className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-800">
          <ChevronRight className={`w-3 h-3 transition-transform ${show ? "rotate-90" : ""}`} />Values
        </button>
        {show && <div className="mt-2 max-h-48 overflow-y-auto thin-scroll" data-testid={`chart-${id}-values`}>{values}</div>}
      </div>
      <SourceFooter source={source} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl bg-white" data-testid={`chart-${id}-dialog`}>
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <div className="h-[520px]">{big ? big(520) : children(520)}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ValueTable = ({ rows }) => (
  <table className="w-full text-[11px]"><tbody>
    {rows.map(([k, c, v]) => <tr key={k} className="border-t border-slate-100"><td className="py-1 text-slate-700">{k}</td><td className="py-1 num text-right text-slate-500">{c} wires</td><td className="py-1 num text-right text-slate-900">{fmtUSD(v)}</td></tr>)}
  </tbody></table>
);

export function TopClients({ q }) {
  const data = q.top_clients;
  const chart = (h) => (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid horizontal={false} stroke="#eef2f6" />
        <XAxis type="number" tickFormatter={(v) => fmtUSD(v, true)} tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="client" width={200} interval={0} tick={{ fontSize: 10, fill: "#334155" }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v) => fmtUSD(v)} cursor={{ fill: "#f1f5f9" }} />
        <Bar dataKey="value" fill="#3f8f6b" radius={[0, 4, 4, 0]} barSize={14} />
      </BarChart>
    </ResponsiveContainer>
  );
  return (
    <ChartCard id="top-clients" title="Top clients by amount" sub="Our accounts, ranked by dollars moved" source={q.source}
      values={<ValueTable rows={data.map((c) => [`${c.client} · ${c.account}`, c.count, c.value])} />}>{chart}</ChartCard>
  );
}

export function DirectionMix({ q }) {
  const data = q.direction_mix.filter((d) => d.value > 0);
  const total = data.reduce((s, d) => s + d.value, 0);
  const pct = Object.fromEntries(q.direction_mix.map((d) => [d.direction, total ? Math.round((d.value / total) * 100) : 0]));
  const big = [...q.direction_mix].sort((a, b) => b.value - a.value)[0];
  if (total && big) pct[big.direction] += 100 - Object.values(pct).reduce((s, v) => s + v, 0);
  const chart = (h) => (
    <div className="flex items-center h-full" style={{ height: h }}>
      <ResponsiveContainer width="70%" height={h}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="direction" innerRadius="55%" outerRadius="85%" stroke="#fff" strokeWidth={2} startAngle={90} endAngle={-270}>
            {data.map((d) => <Cell key={d.direction} fill={DIR_COLOR[d.direction]} />)}
          </Pie>
          <Tooltip formatter={(v) => `${fmtUSD(v, true)} (${((v / total) * 100).toFixed(1)}%)`} />
        </PieChart>
      </ResponsiveContainer>
      <div className="space-y-2" data-testid="direction-legend">
        {q.direction_mix.map((d) => (
          <div key={d.direction} className="flex items-center gap-2 text-xs text-slate-700">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: DIR_COLOR[d.direction] }} />{d.direction}
            <span className="num text-slate-500" data-testid={`direction-pct-${d.direction.toLowerCase()}`}>{pct[d.direction]}%</span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <ChartCard id="direction-mix" title="Direction mix" sub="Incoming, outgoing and internal as a share of the total" source={q.source}
      values={<ValueTable rows={q.direction_mix.map((d) => [d.direction, d.count, d.value])} />}>{chart}</ChartCard>
  );
}
